// Forced 2FA enrollment (Phase 17.E). Managers are routed here by RequireManager2FA
// until they have a confirmed TOTP device. Wraps the existing /auth/2fa/enroll +
// /auth/2fa/confirm; on success refreshes /auth/me (so the gate re-opens) and lands
// the manager on their fund (get-started). Reachable only when logged in
// (RequireAuth) but OUTSIDE the RequireManager2FA gate, so there's no redirect loop.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const home = user?.role === 'manager' ? '/fund' : '/dashboard';
  const [enroll, setEnroll] = useState<{ qr_data_url: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  // Already enrolled (landed here by accident) → go home.
  useEffect(() => {
    if (user && user.two_factor_enabled) navigate(home, { replace: true });
  }, [user, home, navigate]);

  // Auto-begin enrollment — forced setup, so show the QR immediately. Wait for
  // /auth/me to resolve first: firing for a null (still-loading) or already-
  // enrolled user would hit the backend's 409 "already enabled" dead-end.
  useEffect(() => {
    if (!user || started.current || user.two_factor_enabled) return;
    started.current = true;
    api<{ qr_data_url: string }>('/auth/2fa/enroll', { method: 'POST', body: {} })
      .then(setEnroll)
      .catch(async (e: any) => {
        // 409 = a device already exists (stale state / back-button) — not an
        // error: refresh /auth/me so the gate re-opens and bounce home.
        if (e?.status === 409) { await refreshUser(); navigate(home, { replace: true }); return; }
        setError(e?.payload?.detail || 'Could not start enrollment. Please retry.');
      });
  }, [user, home, navigate, refreshUser]);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/2fa/confirm', { method: 'POST', body: { code: code.trim() } });
      const me = await refreshUser();  // so RequireManager2FA sees two_factor_enabled=true
      // Defensive: if /auth/me still reports 2FA off, navigating home would
      // bounce straight back here. Surface a retry instead of looping.
      if (me && me.role === 'manager' && !me.two_factor_enabled) {
        setError('Enrollment did not take effect. Please try again.');
        return;
      }
      navigate(home, { replace: true });
    } catch (err: any) {
      setError(err?.payload?.detail || 'Invalid code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto' }}><Brand onClick={() => navigate(home)} /></div>
      </header>
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '64px var(--pad-x)' }}>
        <div style={{ width: '100%', maxWidth: 460 }}>
          <Kicker>SECURE YOUR ACCOUNT</Kicker>
          <h1 className="page-title" style={{ margin: '8px 0 16px' }}><em>Two-factor</em> setup.</h1>
          <p className="serif-body" style={{ margin: '0 0 28px', fontSize: 16, color: 'var(--ink-2)' }}>
            Two-factor authentication is required for fund managers. Scan the code with an
            authenticator app, then enter the 6-digit code to finish.
          </p>

          {error && (
            <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, color: 'var(--accent)', marginBottom: 20 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
            {enroll ? (
              <img src={enroll.qr_data_url} alt="Two-factor QR code" width={160} height={160}
                   style={{ border: 'var(--hairline)' }} />
            ) : (
              <div style={{ width: 160, height: 160, border: 'var(--hairline)', display: 'grid', placeItems: 'center' }}>
                <span className="label">{error ? '—' : 'Loading…'}</span>
              </div>
            )}
            <form onSubmit={confirm} style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 240, flex: 1 }}>
              <div className="field">
                <label className="field-label">Authentication code</label>
                <input className="field-input num" inputMode="numeric" autoComplete="one-time-code"
                       value={code} onChange={e => setCode(e.target.value)} required autoFocus />
              </div>
              <button type="submit" className="btn" disabled={busy || !enroll}>
                {busy ? 'Verifying…' : <>Finish setup <span className="btn-arrow">→</span></>}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
